import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { addToCart, getToken, removeToken, removeUser, addToWishlist, removeFromWishlist, getWishlist } from '../api/auth';
import { Heart } from 'lucide-react';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BADGE_COLORS = {
  New:        { bg: '#1C1A18', color: '#fff' },
  Bestseller: { bg: '#B08D57', color: '#fff' },
  Sale:       { bg: '#C0392B', color: '#fff' },
  Limited:    { bg: '#2C2C2C', color: '#D4AF78' },
  Trending:   { bg: '#B08D57', color: '#fff' },
  Hot:        { bg: '#C0392B', color: '#fff' },
  Exclusive:  { bg: '#1C1A18', color: '#D4AF78' },
};

const getProductImages = (product) => {
  const fields = [product.image_1, product.image_2, product.image_3].filter(Boolean);
  const jsonImages = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  return [...new Set([...fields, ...jsonImages])];
};

const ImageSwiper = ({ product }) => {
  const imgs = getProductImages(product);
  const count = imgs.length;
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef(null);

  const go = (dir, e) => {
    e.stopPropagation();
    setCurrent(c => (c + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].screenX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].screenX;
    if (Math.abs(diff) > 40) go(diff > 0 ? 1 : -1, e);
    touchStartX.current = null;
  };

  return (
    <div className="pp-slide-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="pp-slide-strip" style={{ transform: `translateX(-${current * 100}%)` }}>
        {imgs.map((src, idx) => (
          <img key={idx} src={src} alt={`${product.name} ${idx + 1}`} loading="lazy" />
        ))}
      </div>
      {count > 1 && (
        <>
          <button className="pp-sarrow pp-sarrow-l" onClick={e => go(-1, e)} aria-label="Previous image">←</button>
          <button className="pp-sarrow pp-sarrow-r" onClick={e => go(1, e)} aria-label="Next image">→</button>
        </>
      )}
    </div>
  );
};

const SORT_OPTIONS = [
  { value: 'default',   label: 'Default'         },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc',label: 'Price: High to Low' },
  { value: 'newest',    label: 'Newest First'    },
  { value: 'name_asc',  label: 'Name: A to Z'   },
];

const ProductsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState(searchParams.get('q') || '');
  const [sort, setSort]               = useState('default');
  const [added, setAdded]             = useState({});
  const [wishlist, setWishlist]       = useState(new Set());
  const { formatPrice }               = useCurrency();

  useEffect(() => {
    document.title = 'All Products | Aamira';
    
    if (getToken()) {
      getWishlist()
        .then(items => setWishlist(new Set(items.map(item => item.id))))
        .catch(() => {});
    }
    
    fetch(`${API}/products?active=true`)
      .then(r => r.json())
      .then(data => { if (data.success) setAllProducts(data.products); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // sync search from URL param
  useEffect(() => {
    const q = searchParams.get('q') || '';
    setSearch(q);
  }, [searchParams]);

  const handleSearch = (val) => {
    setSearch(val);
    if (val) setSearchParams({ q: val });
    else setSearchParams({});
  };

  const filtered = allProducts
    .filter(p => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        p.name?.toLowerCase().includes(q) ||
        (p.colors || []).some(c => c.toLowerCase().includes(q)) ||
        p.badge?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const pa = a.sale_price || a.original_price;
      const pb = b.sale_price || b.original_price;
      if (sort === 'price_asc')  return pa - pb;
      if (sort === 'price_desc') return pb - pa;
      if (sort === 'newest')     return new Date(b.created_at) - new Date(a.created_at);
      if (sort === 'name_asc')   return a.name.localeCompare(b.name);
      return 0;
    });

  const handleAddToCart = async (e, p) => {
    e.stopPropagation();
    if (!getToken()) { navigate('/account'); return; }
  const colorCount = [...new Set(p.colors?.filter(c => c && c.trim()) ?? [])].length;
  const sizeCount  = [...new Set(p.sizes?.filter(s => s && s.trim()) ?? [])].length;

  if (colorCount > 1 || sizeCount > 1) {
    navigate(`/products/${p.id}`);
    return;
  }
    try {
      await addToCart(p.id, 1);
      setAdded(prev => ({ ...prev, [p.id]: true }));
      setTimeout(() => setAdded(prev => ({ ...prev, [p.id]: false })), 1800);
      window.dispatchEvent(new Event('cart-updated')); 
    } catch (err) {
      const msg = err.message?.toLowerCase() || '';
      if (msg.includes('invalid') || msg.includes('expired') || msg.includes('token') || msg.includes('unauthorized')) {
        removeToken(); removeUser(); navigate('/account');
      }
    }
  };

  const handleWishlist = async (productId) => {
    if (!getToken()) { navigate('/account'); return; }
    const isIn = wishlist.has(productId);
    setWishlist(prev => {
      const set = new Set(prev);
      isIn ? set.delete(productId) : set.add(productId);
      return set;
    });
    try {
      if (isIn) await removeFromWishlist(productId);
      else await addToWishlist(productId);
    } catch {
      setWishlist(prev => {
        const set = new Set(prev);
        isIn ? set.add(productId) : set.delete(productId);
        return set;
      });
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');

        .pp-page {
          background: #FAF8F4; min-height: 100vh;
          padding-top: 162px; font-family: 'Jost', sans-serif;
        }

        /* Search + Sort bar */
      
       .pp-bar {
  position: sticky; top: 162px; z-index: 100;
  background: #FAF8F4;
  border-bottom: 1px solid rgba(176,141,87,0.15);
  padding: 14px clamp(20px,5vw,64px);
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  box-shadow: 0 6px 0 6px #FAF8F4;
}
        .pp-search-wrap {
          flex: 1; min-width: 200px; position: relative;
        }
        .pp-search-icon {
          position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
          color: #B08D57; pointer-events: none;
        }
        .pp-search {
          width: 100%; background: #fff;
          border: 1px solid rgba(176,141,87,0.25);
          padding: 10px 14px 10px 40px;
          font-family: 'Jost', sans-serif; font-size: 13px; color: #1C1A18;
          outline: none; transition: border-color 0.2s;
        }
        .pp-search:focus { border-color: #B08D57; }
        .pp-search::placeholder { color: #AEA39A; }
        .pp-sort {
          background: #fff; border: 1px solid rgba(176,141,87,0.25);
          padding: 10px 14px; font-family: 'Jost', sans-serif;
          font-size: 12px; color: #1C1A18; outline: none;
          cursor: pointer; transition: border-color 0.2s; min-width: 180px;
        }
        .pp-sort:focus { border-color: #B08D57; }
        .pp-count {
          font-size: 11px; font-weight: 500; letter-spacing: 0.12em;
          text-transform: uppercase; color: #8A7E72; white-space: nowrap;
        }

        /* Grid */
        .pp-inner {
          max-width: 1280px; margin: 0 auto;
          padding: 32px clamp(20px,5vw,64px) 80px;
        }
        .pp-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
          gap: 20px;
        }

        /* Card */
        .pp-card {
          background: #fff; cursor: pointer;
          transition: box-shadow 0.3s, transform 0.25s;
          border: 1px solid rgba(176,141,87,0.1);
          position: relative;
        }
        .pp-card:hover {
          box-shadow: 0 12px 40px rgba(28,26,24,0.10);
          transform: translateY(-3px);
        }
        .pp-img-wrap {
          position: relative; width: 100%; height: 260px; overflow: hidden;
          background: #F0EAE0;
        }
        .pp-slide-wrap { width: 100%; height: 100%; position: relative; overflow: hidden; }
        .pp-slide-strip { display: flex; width: 100%; height: 100%; transition: transform 0.35s ease; }
        .pp-slide-strip img { flex: 0 0 100%; width: 100%; height: 100%; object-fit: cover; object-position: center; display: block; }
        .pp-sarrow {
          position: absolute; top: 50%; transform: translateY(-50%); width: 30px; height: 30px;
          border: none; border-radius: 50%; background: rgba(255,255,255,0.9); color: #1C1A18;
          cursor: pointer; display: flex; align-items: center; justify-content: center; z-index: 4;
          opacity: 0; transition: opacity 0.2s, background 0.2s;
        }
        .pp-img-wrap:hover .pp-sarrow,
        .pp-card:hover .pp-sarrow { opacity: 1; }
        .pp-sarrow:hover { background: #fff; }
        .pp-sarrow-l { left: 10px; }
        .pp-sarrow-r { right: 10px; }
        .pp-badge {
          position: absolute; top: 12px; left: 12px;
          font-size: 8px; font-weight: 700; letter-spacing: 0.18em;
          text-transform: uppercase; padding: 4px 10px; z-index: 2;
        }
        .pp-discount {
          font-size: 8px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase;
          color: #C0392B; background: rgba(192,57,43,0.08); padding: 2px 5px;
        }
        .pp-wish-btn {
          position: absolute; top: 12px; right: 12px; z-index: 3;
          width: 30px; height: 30px; background: rgba(250,248,244,0.9);
          border: none; border-radius: 50%; display: flex; align-items: center; justify-content: center;
          cursor: pointer;
        }

        /* Body */
        .pp-card-body { padding: 16px 18px 4px; border-top: 1px solid #f5f0e8; }
        .pp-name {
          font-family: 'Playfair Display', serif;
          font-size: 15px; font-weight: 600; color: #1C1A18;
          margin-bottom: 8px; line-height: 1.3;
        }
        .pp-prices {
          display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
          margin-bottom: 12px;
        }
        .pp-price { font-family: 'Jost', sans-serif; font-size: 15px; font-weight: 600; color: #B08D57; letter-spacing: 0.04em; }
        
        .pp-original {
          font-size: 12px; color: #AEA39A;
          text-decoration: line-through; font-weight: 300;
        }

        /* ATC */
        .pp-atc {
          width: 100%; background: #1C1A18; color: #fff;
          border: none; font-family: 'Jost', sans-serif;
          font-size: 9px; font-weight: 600; letter-spacing: 0.2em;
          text-transform: uppercase; padding: 12px;
          cursor: pointer; transition: background 0.2s; display: block;
          margin-bottom: 0;
        }
        .pp-atc:hover { background: #B08D57; }
        .pp-atc.added { background: #8A6D3E; }

        /* Empty / Loading */
        .pp-empty {
          text-align: center; padding: 80px 20px; color: #8A7E72;
        }
        .pp-empty-title {
          font-family: 'Playfair Display', serif;
          font-size: 22px; color: #3a3530; margin-bottom: 8px;
        }
        .pp-loading {
          text-align: center; padding: 80px;
          font-size: 13px; color: #9A9186; letter-spacing: 0.1em;
        }

      @media (max-width: 860px) {
        .pp-page { padding-top: 112px; }
        .pp-bar  { top: 112px; z-index: 100; background: #FAF8F4; box-shadow: 0 6px 0 6px #FAF8F4; }
        .pp-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
        .pp-img-wrap { height: 200px; }
      }
      @media (max-width: 480px) {
        .pp-page { padding-top: 106px; }
        .pp-bar  { top: 106px; z-index: 100; background: #FAF8F4; box-shadow: 0 6px 0 6px #FAF8F4; }
        .pp-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
        .pp-img-wrap { height: 170px; }
      }
      `}</style>

      <div className="pp-page">

        {/* Search + Sort bar */}
        <div className="pp-bar">
          <div className="pp-search-wrap">
            <svg className="pp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="pp-search"
              placeholder="Search by name, color, badge…"
              value={search}
              onChange={e => handleSearch(e.target.value)}
            />
          </div>
          <select className="pp-sort" value={sort} onChange={e => setSort(e.target.value)}>
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {!loading && (
            <span className="pp-count">{filtered.length} product{filtered.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        <div className="pp-inner">
          {loading ? (
            <div className="pp-loading">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="pp-empty">
              <div className="pp-empty-title">No products found</div>
              <p style={{ fontSize: 13 }}>Try a different search term.</p>
            </div>
          ) : (
            <div className="pp-grid">
              <AnimatePresence>
                {filtered.map((p, i) => {
                  const hasDiscount  = p.sale_price && p.sale_price < p.original_price;
                  const displayPrice = hasDiscount ? p.sale_price : p.original_price;
                  const mrpPrice     = hasDiscount ? p.original_price : null;
                  const discountPct  = mrpPrice ? Math.round((1 - displayPrice / mrpPrice) * 100) : 0;
                  const bc           = p.badge ? (BADGE_COLORS[p.badge] || BADGE_COLORS['New']) : null;

                  return (
                    <motion.div
                      key={p.id}
                      className="pp-card"
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.3) }}
                      onClick={() => navigate(`/products/${p.id}`)}
                    >
                      <div className="pp-img-wrap">
                        <ImageSwiper product={p} />
                        <button className="pp-wish-btn"
                          onClick={e => { e.stopPropagation(); handleWishlist(p.id); }}
                          aria-label={wishlist.has(p.id) ? 'Remove from wishlist' : 'Add to wishlist'}>
                          <Heart size={13} fill={wishlist.has(p.id) ? '#C0392B' : 'none'} color={wishlist.has(p.id) ? '#C0392B' : '#5C5247'} />
                        </button>
                        {bc && (
                          <span className="pp-badge" style={{ background: bc.bg, color: bc.color }}>
                            {p.badge}
                          </span>
                        )}
                      {p.stock_qty === 0 && (
                        <span style={{
                          position: 'absolute', bottom: 0, left: 0, right: 0,
                          background: 'rgba(28,26,24,0.82)', color: '#fff',
                          fontSize: 8, fontWeight: 700, letterSpacing: '0.18em',
                          textTransform: 'uppercase', padding: '6px', textAlign: 'center', zIndex: 4,
                        }}>Sold Out</span>
                      )}
                      </div>

                      <div className="pp-card-body">
                        <div className="pp-name">{p.name}</div>
                        <div className="pp-prices">
                          <span className="pp-price">{formatPrice(displayPrice)}</span>
                          {mrpPrice && (
                            <span className="pp-original">{formatPrice(mrpPrice)}</span>
                          )}
                          {discountPct > 0 && <span className="pp-discount">{discountPct}% off</span>}
                        </div>
                      </div>

                      <button
                      className={`pp-atc${added[p.id] ? ' added' : ''}`}
                      disabled={p.stock_qty === 0}
                      style={p.stock_qty === 0 ? { background: '#9A9186', cursor: 'not-allowed' } : {}}
                      onClick={e => {
                        e.stopPropagation();
                        if (p.stock_qty === 0) return;
                        handleAddToCart(e, p);
                      }}
                    >
                      {added[p.id] ? '✓ Added to Cart'
                        : p.stock_qty === 0 ? 'Sold Out'
                        : ([...new Set(p.colors?.filter(c => c && c.trim()) ?? [])].length > 1 ||
                        [...new Set(p.sizes?.filter(s => s && s.trim()) ?? [])].length > 1) ? 'Select Options'
                        : 'Add to Cart'}
                    </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductsPage;